/**
 * Chat de partie. Bouton flottant en bas a droite, panneau repliable.
 *
 * Ferme par defaut : pendant DRAWING et GUESSING l'attention doit rester sur le
 * tableau. Le badge signale les messages non lus sans rien recouvrir, et le
 * panneau ne bloque jamais l'ecran : on peut continuer a jouer avec.
 *
 * Rien n'est persiste : l'historique vit dans le store, il disparait au
 * rechargement comme le reste de l'etat de partie.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { CHAT_MAX_LENGTH, PLAYER_COLORS } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { useCompact } from './useCompact.js';
import { connection } from '../net/connection.js';
import { useRoomStore } from '../store/roomStore.js';

export function ChatPanel(): ReactElement | null {
  const t = useT();
  const room = useRoomStore((state) => state.room);
  const messages = useRoomStore((state) => state.chat);
  const playerId = useRoomStore((state) => state.playerId);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(0);

  /**
   * Sur telephone, le bouton flottant tombe dans le pouce, juste au-dessus de
   * Valider, pendant qu'on dessine. On l'efface le temps de la phase de dessin :
   * il revient des la recherche, ou l'on a de nouveau envie de parler.
   */
  const compact = useCompact();
  const drawing = room?.phase === 'DRAWING' || room?.phase === 'COLLECTING';
  const hidden = compact && drawing && !open;

  const listRef = useRef<HTMLDivElement | null>(null);
  const seenCount = useRef(0);

  // Compteur de non-lus : seulement quand le panneau est ferme.
  useEffect(() => {
    if (open) {
      seenCount.current = messages.length;
      setUnread(0);
      return;
    }
    setUnread(messages.length - seenCount.current);
  }, [messages, open]);

  // Colle au dernier message a l'ouverture et a chaque arrivee.
  useEffect(() => {
    if (!open || listRef.current === null) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (text === '') return;
    connection.send({ type: 'chat', text });
    setDraft('');
  }, [draft]);

  // Hors partie, le chat n'a pas de destinataire.
  if (room === null) return null;

  return (
    <>
      <button
        type="button"
        className="chat-toggle"
        data-hidden={hidden}
        aria-expanded={open}
        aria-label={t('chat.title')}
        onClick={() => setOpen((value) => !value)}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            d="M4 5h16v11H9l-5 4V5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 && !open && (
          <span className="chat-toggle__badge mono-tabular">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <section className="chat" aria-label={t('chat.title')}>
          <header className="chat__header">
            <span>{t('chat.title')}</span>
            <button
              type="button"
              className="chat__close"
              aria-label={t('common.close')}
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>

          <div className="chat__list" ref={listRef}>
            {messages.length === 0 && <p className="chat__empty">{t('chat.empty')}</p>}
            {messages.map((message) => {
              const author = room.players.find((player) => player.id === message.playerId);
              const color = PLAYER_COLORS[(author?.colorIndex ?? 0) % PLAYER_COLORS.length];
              return (
                <p className="chat__line" key={message.id} data-self={message.playerId === playerId}>
                  <span className="chat__author" style={{ color }}>
                    {message.nickname}
                  </span>
                  {/* React echappe : jamais de dangerouslySetInnerHTML ici. */}
                  <span className="chat__text">{message.text}</span>
                </p>
              );
            })}
          </div>

          <form
            className="chat__form"
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            <input
              className="chat__input"
              value={draft}
              maxLength={CHAT_MAX_LENGTH}
              placeholder={t('chat.placeholder')}
              aria-label={t('chat.placeholder')}
              onChange={(event) => setDraft(event.target.value)}
            />
            <button type="submit" className="chat__send" disabled={draft.trim() === ''}>
              {t('chat.send')}
            </button>
          </form>
        </section>
      )}
    </>
  );
}
